import { apiClient as baseApiClient } from '#/api/client.tsx';
import type {
  EtcTicket,
  EtcTicketOperatorNote,
  EtcTicketState,
  EtcTicketSummary,
} from '../model';
import { format } from 'date-fns';

const cockpitApiClient = baseApiClient.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/cockpit/auto-pay`,
});

const TICKETS_BASE = '/etc-auto-pay-tickets';

type ISODateTime = string;

type ListTicketsParams = {
  page?: number;
  pageSize?: number;
  state?: EtcTicketState[];
  accountEmail?: string;
  plateNumber?: string;
  idNumber?: string;
  taxIdNumber?: string;
  createDateTimeFrom?: ISODateTime;
  createDateTimeTo?: ISODateTime;
};

type CompleteTicketBody = {
  completeDateTime: ISODateTime;
};

// --- Response types ---

type ChargeTicketResponse = {
  id: string;
  state: string;
  paymentId: string | null;
};

type TicketStateChangeResponse = Pick<EtcTicket, 'id' | 'state'>;

// --- Endpoints ---

type ListTicketsResponse = {
  data: EtcTicketSummary[];
  extra: {
    totalElement: number;
    totalPage: number;
  };
};

export const createTicket = () => {
  const body = {
    accountId: '422fa263-033a-4081-8f8e-287ac951850a',
  };

  return cockpitApiClient.post(`${TICKETS_BASE}`, body).then((res) => {
    return res.data.data;
  });
};

// === info getter ===
export const listTickets = (
  params: ListTicketsParams = {} as ListTicketsParams,
) => {
  return cockpitApiClient
    .get<ListTicketsResponse>(`${TICKETS_BASE}`, {
      params,
      paramsSerializer(params) {
        const cleaned = Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== undefined),
        );

        const urlParams = new URLSearchParams(cleaned);
        return urlParams.toString();
      },
    })
    .then((res) => res.data);
};

export const getTicketById = (id: string) => {
  return cockpitApiClient
    .get<{ data: EtcTicket }>(`${TICKETS_BASE}/${id}`)
    .then((res) => res.data.data);
};

type ExportTicketsParams = {
  from?: ISODateTime;
  to?: ISODateTime;
};

type ExportCompletedTicketParams = Required<ExportTicketsParams>;

export const exportPendingTickets = (params: ExportTicketsParams = {}) => {
  return cockpitApiClient
    .get<Blob>(`${TICKETS_BASE}/excel`, {
      params: { state: 'PENDING', ...params },
      responseType: 'blob',
      paramsSerializer(params) {
        const cleaned = Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== undefined),
        );

        const urlParams = new URLSearchParams(cleaned);
        return urlParams.toString();
      },
    })
    .then((res) => {
      const currentTimestamp = format(new Date(), 'yyyyMMddHHmmss');
      const filename = `ETC通行費待繳_${currentTimestamp}`;
      return {
        blob: res.data,
        filename,
      };
    });
};

export const exportCompletedTicket = (params: ExportCompletedTicketParams) => {
  return cockpitApiClient
    .get<Blob>(`${TICKETS_BASE}/excel`, {
      params: { state: 'COMPLETED', ...params },
      responseType: 'blob',
    })
    .then((res) => {
      const currentTimestamp = format(new Date(), 'HHmmss');
      const startDate = format(new Date(params.from), 'yyyyMMdd');
      const endDate = format(new Date(params.to), 'yyyyMMdd');
      const filename = `ETC通行費_${startDate}-${endDate}_${currentTimestamp}`;
      return {
        blob: res.data,
        filename,
      };
    });
};

// === finish th query of the ticket ===

type FailedMutationBody = {
  reason: string;
};

export const markNoFee = (id: string, body: FailedMutationBody) =>
  cockpitApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/no-fee`, body)
    .then((res) => res.data.data);

export const markQueryFailed = (id: string, body: FailedMutationBody) =>
  cockpitApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/query-failed`, body)
    .then((res) => res.data.data);

type ChargeTicketBody = {
  amount: number;
};

export const chargeTicket = (id: string, body: ChargeTicketBody) =>
  cockpitApiClient
    .post<{
      data: ChargeTicketResponse;
    }>(`${TICKETS_BASE}/${id}/charge`, body)
    .then((res) => res.data.data);

export const completeTicket = (id: string, body: CompleteTicketBody) =>
  cockpitApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/complete`, body)
    .then((res) => res.data.data);

type AppendOperatorNoteBody = Pick<EtcTicketOperatorNote, 'content'>;

type AddOperatorNoteResponse = EtcTicketOperatorNote;

export const appendOperatorNote = (id: string, body: AppendOperatorNoteBody) =>
  cockpitApiClient
    .post<{
      data: AddOperatorNoteResponse;
    }>(`${TICKETS_BASE}/${id}/operator-notes`, body)
    .then((res) => res.data.data);

export type {
  ListTicketsParams,
  ExportTicketsParams,
  ExportCompletedTicketParams,
  ChargeTicketBody,
  CompleteTicketBody,
  AppendOperatorNoteBody as AddOperatorNoteBody,
  ListTicketsResponse,
  ChargeTicketResponse,
  TicketStateChangeResponse,
  AddOperatorNoteResponse,
};
